import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import styles from './ContactCTA.module.css';

// Input sanitization helper
const sanitizeInput = (input: string): string => {
  return input.trim().replace(/[<>]/g, '');
};

// Email validation helper
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= 254;
};

export const ContactCTA: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: '',
    consent: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus('idle');

    // Client-side validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.message) {
      setSubmitStatus('error');
      setSubmitting(false);
      return;
    }

    if (!isValidEmail(formData.email)) {
      setSubmitStatus('error');
      setSubmitting(false);
      return;
    }

    if (formData.firstName.length > 100 || formData.lastName.length > 100) {
      setSubmitStatus('error');
      setSubmitting(false);
      return;
    }

    if (formData.message.length > 5000) {
      setSubmitStatus('error');
      setSubmitting(false);
      return;
    }

    try {
      const formPayload = new URLSearchParams({
        'form-name': 'contact-form',
        firstName: sanitizeInput(formData.firstName),
        lastName: sanitizeInput(formData.lastName),
        email: formData.email.trim(),
        message: sanitizeInput(formData.message),
        consent: formData.consent ? 'yes' : 'no',
      });

      // Check if we're in development (localhost)
      const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      if (isDevelopment) {
        // Simulate form submission in development
        console.log('Form submission (development mode):', {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          message: formData.message,
          consent: formData.consent
        });
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          message: '',
          consent: false
        });
      } else {
        // Production: submit to Netlify
        const response = await fetch('/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formPayload.toString(),
        });

        if (response.ok) {
          setSubmitStatus('success');
          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            message: '',
            consent: false
          });
        } else {
          setSubmitStatus('error');
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <div className={styles.contactContainer}>
      <div className={styles.contactInfo}>
        <h3 className={styles.contactTitle}>{t('home.contactCta.title')}</h3>
        <p className={styles.contactSubtitle}>{t('home.contactCta.subtitle')}</p>
        <div className={styles.contactDetails}>
          <a href="mailto:info@gcbusiness.space" className={styles.email}>info@gcbusiness.space</a>
          <a href="https://wa.me/442046219434" target="_blank" rel="noopener noreferrer" className={styles.whatsappButton} aria-label="GCB PayConcierge WhatsApp (opens in new window)">
            <MessageCircle size={20} />
            <span>GCB PayConcierge WhatsApp</span>
          </a>
        </div>
      </div>
      
      <form 
        className={styles.contactForm} 
        onSubmit={handleSubmit}
        name="contact-form"
        data-netlify="true"
        data-netlify-honeypot="bot-field"
      >
        <input type="hidden" name="form-name" value="contact-form" />
        <p style={{ display: 'none' }}>
          <label>
            Don't fill this out if you're human: <input name="bot-field" />
          </label>
        </p>
        {submitStatus === 'success' && (
          <div style={{ 
            padding: 'var(--spacing-md)', 
            background: '#d4edda', 
            color: '#155724', 
            borderRadius: '8px', 
            marginBottom: 'var(--spacing-md)' 
          }}>
            Thank you! Your message has been sent successfully.
          </div>
        )}
        {submitStatus === 'error' && (
          <div style={{ 
            padding: 'var(--spacing-md)', 
            background: '#f8d7da', 
            color: '#721c24', 
            borderRadius: '8px', 
            marginBottom: 'var(--spacing-md)' 
          }}>
            Sorry, there was an error sending your message. Please try again or contact us directly.
          </div>
        )}
        <div className={styles.formRow}>
          <div className={styles.inputGroup}>
            <label htmlFor="firstName" className={styles.label}>{t('home.contactCta.form.firstName')}</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              className={styles.input}
              required
              maxLength={100}
            />
          </div>
          <div className={styles.inputGroup}>
            <label htmlFor="lastName" className={styles.label}>{t('home.contactCta.form.lastName')}</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              className={styles.input}
              required
              maxLength={100}
            />
          </div>
        </div>
        
        <div className={styles.inputGroup}>
          <label htmlFor="email" className={styles.label}>{t('home.contactCta.form.email')}</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={styles.input}
            required
            maxLength={254}
          />
        </div>
        
        <div className={styles.inputGroup}>
          <label htmlFor="message" className={styles.label}>{t('home.contactCta.form.message')}</label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            className={styles.textarea}
            rows={4}
            required
            maxLength={5000}
          />
        </div>
        
        <div className={styles.consentGroup}>
          <input
            type="checkbox"
            id="consent"
            name="consent"
            checked={formData.consent}
            onChange={handleInputChange}
            className={styles.checkbox}
            required
          />
          <label htmlFor="consent" className={styles.consentLabel}>
            {t('home.contactCta.form.consent')}
          </label>
        </div>
        
        <Button type="submit" className={styles.sendButton} disabled={submitting}>
          {submitting ? 'Sending...' : t('home.contactCta.form.sendButton')}
        </Button>
      </form>
    </div>
  );
};

